import React, { useEffect, useRef, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Send, Loader2, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

export default function MarketingAgentChat() {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const conv = await base44.agents.createConversation({
          agent_name: 'marketing_assistant',
          metadata: { name: 'Marketing Assistant' },
        });
        if (!active) return;
        setConversation(conv);
        setMessages(conv.messages || []);
      } catch (e) {
        setError('Could not start the marketing assistant. Make sure the agent is enabled.');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!conversation?.id) return;
    const unsub = base44.agents.subscribeToConversation(conversation.id, (data) => {
      setMessages(data.messages || []);
      setBusy(false);
    });
    return () => unsub();
  }, [conversation?.id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || !conversation || busy) return;
    setInput('');
    setBusy(true);
    try {
      const updated = await base44.agents.addMessage(conversation, { role: 'user', content: text });
      setConversation(updated);
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white/[0.02] border border-white/5 flex flex-col h-[600px]">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/5">
          <Bot className="w-4 h-4 text-primary" />
          <p className="text-white text-xs font-bold tracking-widest">MARKETING AI ASSISTANT</p>
          <span className="text-white/20 text-[10px] ml-auto">can check prices, write SEO &amp; edit pages</span>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && <p className="text-red-400 text-sm">{error}</p>}
          {messages.length === 0 && !error && (
            <p className="text-white/30 text-sm">Ask me to check competitor prices, write product SEO, or optimise a category page — e.g. “check competitor prices for our most expensive products” or “write SEO for the homepage”.</p>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={`max-w-[85%] px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-primary/20 border border-primary/30 text-white' : 'bg-white/5 border border-white/10 text-white/80'}`}>
                {m.content ? <ReactMarkdown>{m.content}</ReactMarkdown> : (m.tool_calls?.length ? <span className="text-white/40 italic">Working…</span> : null)}
                {m.tool_calls?.map((tc, j) => (
                  <div key={j} className="mt-1.5 text-[10px] text-white/40 border-t border-white/10 pt-1">⚙ {tc.name} · {tc.status}</div>
                ))}
              </div>
            </div>
          ))}
          <div ref={scrollRef} />
        </div>
        <div className="p-3 border-t border-white/5 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask the marketing assistant…"
            className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 placeholder:text-white/20 focus:border-primary/40 outline-none"
          />
          <button onClick={send} disabled={busy || !conversation} className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-4 disabled:opacity-40 flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} SEND
          </button>
        </div>
      </div>
    </div>
  );
}